import { HlsPlayerState, AudioPlayerConfig } from "@/types/types";
import Hls, { ErrorData } from "hls.js";

type AudioEventMap = keyof HTMLMediaElementEventMap;

export class HlsAudioPlayer {
  private audio: HTMLAudioElement;
  private hls: Hls;
  private state: HlsPlayerState;

  constructor(config?: Partial<AudioPlayerConfig>) {
    this.audio = new Audio();
    this.hls = new Hls({
      maxBufferLength: 30,
      maxMaxBufferLength: 600,
      maxBufferSize: 60 * 1000 * 1000,
      startLevel: -1,
      debug: false,
      enableWorker: false,
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 20000,
          maxLoadTimeMs: 20000,
          timeoutRetry: {
            maxNumRetry: 3,
            retryDelayMs: 1000,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 3,
            retryDelayMs: 1000,
            maxRetryDelayMs: 8000,
          },
        },
      },
      ...config,
    });

    this.state = {
      isPlaying: false,
      isBuffering: false,
      error: null,
      currentTime: 0,
      duration: 0,
    };
  }

  addEventListener<K extends AudioEventMap>(
    type: K,
    listener: (event: HTMLMediaElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    this.audio.addEventListener(type, listener as EventListener, options);
  }

  removeEventListener<K extends AudioEventMap>(
    type: K,
    listener: (event: HTMLMediaElementEventMap[K]) => void,
    options?: boolean | EventListenerOptions
  ): void {
    this.audio.removeEventListener(type, listener as EventListener, options);
  }

  async play(streamUrl: string): Promise<void> {
    if (Hls.isSupported()) {
      await this.setupHlsPlayback(streamUrl);
    } else if (this.audio.canPlayType("application/vnd.apple.mpegurl")) {
      await this.setupFallbackPlayback(streamUrl);
    } else {
      throw new Error("HLS playback not supported");
    }
  }

  private setupHlsPlayback(streamUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Add event listeners for debugging
      this.hls.on(Hls.Events.MANIFEST_LOADING, () => {
        //        console.log("Manifest loading...");
      });

      this.hls.on(Hls.Events.ERROR, (event, data) => {
        console.error("HLS Error:", {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          response: data.response
            ? {
                code: data.response.code,
                text: data.response.text,
              }
            : null,
        });
      });

      this.hls.on(Hls.Events.MANIFEST_LOADED, () => {
        //        console.log("Manifest loaded");
      });

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        //        console.log("Manifest parsed - attempting playback");
        this.audio
          .play()
          .then(() => {
            this.state.isPlaying = true;
            this.state.duration = this.audio.duration;
            resolve();
          })
          .catch((error) => {
            console.log("Playback failed:", error);
            this.handleError(error);
            reject(error);
          });
      });

      // Add level loading events
      this.hls.on(Hls.Events.LEVEL_LOADING, () => {
        //        console.log("Level loading...");
      });

      this.hls.on(Hls.Events.LEVEL_LOADED, () => {
        //        console.log("Level loaded");
      });

      // Setup media element
      this.audio.addEventListener("loadedmetadata", () => {
        //        console.log("Audio metadata loaded");
      });

      this.audio.addEventListener("canplay", () => {
        //        console.log("Audio can play");
      });

      // Setup error handling
      this.setupErrorHandling();

      // Load the source
      try {
        this.hls.loadSource(streamUrl);
        this.hls.attachMedia(this.audio);
      } catch (error) {
        console.error("Error during HLS setup:", error);
        reject(error);
      }
    });
  }

  private setupErrorHandling(): void {
    this.hls.on(Hls.Events.ERROR, (_: any, data: ErrorData) => {
      console.error("HLS Error:", data);
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.log("Network error - attempting to recover");
            this.hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log("Media error - attempting to recover");
            this.hls.recoverMediaError();
            break;
          default:
            console.log("Fatal error - destroying player");
            this.destroy();
            break;
        }
      }
    });
  }

  private async setupFallbackPlayback(streamUrl: string): Promise<void> {
    try {
      this.audio.src = streamUrl;
      await this.audio.play();
      this.state.isPlaying = true;
    } catch (error) {
      console.error("Fallback playback failed:", error);
      throw error;
    }
  }

  private handleError = (error: Error): void => {
    console.error("Playback error:", error);
    this.state.error = error;
    this.state.isPlaying = false;
  };

  async resume(): Promise<void> {
    try {
      if (!this.audio) {
        throw new Error("Audio element not initialized");
      }

      // Update audio position
      this.audio.currentTime = this.state.currentTime;

      // Attempt to play
      await this.audio.play();

      // Update state after successful play
      this.state = {
        ...this.state,
        isPlaying: true,
        error: null,
        isBuffering: false,
      };
    } catch (error) {
      console.error("Resume failed:", error);
      this.handleError(error as Error);
      throw error;
    }
  }

  async fadeIn(streamUrl: string, durationMillis: number = 1000): Promise<void> {
    try {
      if (!this.audio) {
        throw new Error("Audio element not initialized");
      }

      this.audio.volume = 0;
      await this.play(streamUrl);

      const startTime = performance.now();

      return new Promise((resolve) => {
        const fade = () => {
          const elapsedTime = performance.now() - startTime;
          const volume = Math.min(1, elapsedTime / durationMillis);

          this.audio.volume = volume;

          if (volume < 1) {
            requestAnimationFrame(fade);
          } else {
            resolve();
          }
        };

        fade();
      });
    } catch (error) {
      console.error("Fade in failed:", error);
      this.handleError(error as Error);
      throw error;
    }
  }

  async fadeOut(durationMillis: number = 1000): Promise<void> {
    try {
      if (!this.audio) {
        throw new Error("Audio element not initialized");
      }

      const initialVolume = this.audio.volume;
      const startTime = performance.now();

      return new Promise((resolve) => {
        const fade = () => {
          const elapsedTime = performance.now() - startTime;
          const remainingTime = Math.max(0, durationMillis - elapsedTime);
          const volume = initialVolume * (remainingTime / durationMillis);

          this.audio.volume = volume;

          if (remainingTime > 0) {
            requestAnimationFrame(fade);
          } else {
            this.audio.pause();
            this.audio.volume = initialVolume; // Restore initial volume
            this.state = {
              ...this.state,
              isPlaying: false,
              currentTime: this.audio.currentTime,
              isBuffering: false,
              error: null,
            };
            resolve();
          }
        };

        fade();
      });
    } catch (error) {
      console.error("Fade out failed:", error);
      this.handleError(error as Error);
      throw error;
    }
  }

  async pause(): Promise<void> {
    try {
      if (!this.audio) {
        throw new Error("Audio element not initialized");
      }

      // Pause playback
      this.audio.pause();

      // Update state
      this.state = {
        ...this.state,
        isPlaying: false,
        currentTime: this.audio.currentTime,
        isBuffering: false,
        error: null,
      };
    } catch (error) {
      console.error("Pause failed:", error);
      this.handleError(error as Error);
      throw error;
    }
  }

  getState(): HlsPlayerState {
    // Return an immutable copy of the state
    return Object.freeze({ ...this.state });
  }

  destroy(): void {
    try {
      // Clean up HLS instance
      if (this.hls) {
        this.hls.destroy();
      }

      // Clean up audio element
      if (this.audio) {
        this.audio.pause();
        this.audio.src = "";
        this.audio.remove();
      }

      // Reset state
      this.state = {
        isPlaying: false,
        isBuffering: false,
        error: null,
        currentTime: 0,
        duration: 0,
      };
    } catch (error) {
      console.error("Error during cleanup:", error);
      throw error; // Propagate cleanup errors
    }
  }
}
